const RECOVERY_KEY = 'trackeat.pwa.version-skew-recovery.v1';
const DYNAMIC_IMPORT_FAILURE = /(?:failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script)/i;
const APP_ASSET_PATH = /^\/assets\/.+\.(?:js|css)$/i;
const APP_ASSET_URL = /(?:https?:\/\/[^\s'"()]+\/assets\/[^\s'"()]+\.(?:js|css)(?:\?[^\s'"()]*)?|\/assets\/[^\s'"()]+\.(?:js|css)(?:\?[^\s'"()]*)?)/gi;

type PwaRecoveryHost = Pick<Window, 'addEventListener' | 'sessionStorage'> & {
  location: Pick<Location, 'href' | 'origin' | 'reload'>;
};

function messageFrom(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  if (value && typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
    return value.message;
  }
  return '';
}

function ownAssetPath(host: PwaRecoveryHost, value: string): string | null {
  try {
    const url = new URL(value, host.location.href);
    if (url.origin !== host.location.origin || !APP_ASSET_PATH.test(url.pathname)) return null;
    return url.pathname;
  } catch {
    return null;
  }
}

function assetPathFromMessage(host: PwaRecoveryHost, message: string): string | null {
  for (const match of message.matchAll(APP_ASSET_URL)) {
    const assetPath = ownAssetPath(host, match[0]);
    if (assetPath) return assetPath;
  }
  return null;
}

function isVersionSkewFailure(host: PwaRecoveryHost, reason: unknown): boolean {
  const message = messageFrom(reason);
  return DYNAMIC_IMPORT_FAILURE.test(message) && assetPathFromMessage(host, message) !== null;
}

function recoverOnce(host: PwaRecoveryHost, event: Event): boolean {
  try {
    if (host.sessionStorage.getItem(RECOVERY_KEY) !== null) return false;
    host.sessionStorage.setItem(RECOVERY_KEY, '1');
  } catch {
    // Senza una guardia durevole non ricarichiamo: un loop sarebbe peggiore del
    // messaggio di errore e non dobbiamo mai toccare cache o dati offline.
    return false;
  }

  event.preventDefault();
  host.location.reload();
  return true;
}

/**
 * Recupera soltanto lo skew tra una shell PWA ormai vecchia e un chunk Vite
 * rimosso dal deploy successivo. La guardia e' unica per sessione: un errore
 * successivo resta visibile invece di creare una catena di reload.
 */
export function installPwaVersionSkewRecovery(host: PwaRecoveryHost = window): void {
  host.addEventListener('vite:preloadError', (event) => {
    const preloadEvent = event as Event & { payload?: unknown };
    if (isVersionSkewFailure(host, preloadEvent.payload)) recoverOnce(host, event);
  });

  host.addEventListener('error', (event) => {
    const errorEvent = event as ErrorEvent;
    if (isVersionSkewFailure(host, errorEvent.error ?? errorEvent.message)) recoverOnce(host, event);
  }, true);

  host.addEventListener('unhandledrejection', (event) => {
    const rejectionEvent = event as PromiseRejectionEvent;
    if (isVersionSkewFailure(host, rejectionEvent.reason)) recoverOnce(host, event);
  });
}
